import { map, isNil, isEmpty } from 'lodash';

import * as videoOps from './video-operations';
import * as annotationOps from '../Annotations/annotation-operations';

/**
 * Checks if an annotation exists at the given time and sets a new time if it does.
 *
 * @param {number} newTime - New annotation time for check.
 * @param {Array} annotations - Array with annotations.
 * @returns {boolean} If the time of new annotation is correct or not.
 * @throws Error if something is wrong.
 */
export const checkTimeAnnotation = (newTime, annotations) => {
  if (isNil(newTime) || isNil(annotations)) {
    throw new Error('video or proyected point not defined');
  }
  let check = true;
  map(annotations, (element, index, array) => {
    const timeElement = element.geoJSON.properties.videoTime;
    if (index > 0) {
      const prevElement = array[index - 1];
      const prevTimeElement = prevElement.geoJSON.properties.videoTime;
      if (newTime >= prevTimeElement && newTime <= timeElement) {
        if (
          Math.abs(prevTimeElement - newTime) < 1000000 ||
          Math.abs(timeElement - newTime) < 1000000
        ) {
          check = false;
        }
      }
    }
  });
  if (check === false) {
    checkTimeAnnotation(newTime + 1000000, annotations);
  }
  return Math.round(newTime);
};

/**

 * Calculate the time for the video represented by line.
 *
 * @param {object} videoSelected - The video object.
 * @param {object} proyectedPointTrackVideo - The proyected point over the line with turf.
 * @param {Array} annotations - The annotations of video.
 * @returns {number} The video time.
 * @throws Error if proyected point or video selected is empty.
 */
export const calculateTimeVideoLine = (videoSelected, proyectedPointTrackVideo, annotations) => {
  if (isNil(videoSelected) || isNil(proyectedPointTrackVideo) || isNil(annotations)) {
    throw new Error('video or proyected point not defined');
  }
  const distNewPointOnLine = proyectedPointTrackVideo.properties.location;

  const controlPoints = videoOps.getControlPoints(videoSelected);
  const { features } = controlPoints;

  let time;
  let count = 0;

  map(features, (element, index, array) => {
    const timePoint = element.properties.timeUS;
    const distance = element.properties.distanceTravelled;
    const lastPoint = array[array.length - 1];
    const lastDistance = lastPoint.properties.distanceTravelled;
    const lastTime = lastPoint.properties.timeUS;
    if (index > 0) {
      const prevPoint = array[index - 1];
      const prevTime = prevPoint.properties.timeUS;
      const prevDistance = prevPoint.properties.distanceTravelled;
      if (distNewPointOnLine < distance && count === 0) {
        count += 1;
        time =
          prevTime +
          ((timePoint - prevTime) / (distance - prevDistance)) *
            (distNewPointOnLine - prevDistance);
        if (time === 0) {
          time = 1000000;
        }
      }
      if (distNewPointOnLine >= lastDistance) {
        time = lastTime - 1000000;
      }
    }
  });

  const checkTime = checkTimeAnnotation(time, annotations);
  return checkTime;
};

/**

 * Calculate the time for the video represented by point.
 *
 * @param {Array} annotations - The annotations of video.
 * @param {Array} manyNewAnnotation - The annotations of video.
 * @returns {number} The video time.
 * @throws Error if something is wrong.
 */
export const calculateTimeVideoPoint = (annotations, manyNewAnnotation) => {
  if (isNil(annotations) || isNil(manyNewAnnotation)) {
    throw new Error('annotations or manyNewAnnotation not defined');
  }
  let time = 5000000;
  if (!isEmpty(manyNewAnnotation)) {
    const lastNewPoint = manyNewAnnotation[manyNewAnnotation.length - 1];
    const lastNewTime = lastNewPoint.newTime;
    time = lastNewTime + 5000000;
  }

  const checkTime = checkTimeAnnotation(time, annotations);
  return checkTime;
};

/**
 * Get the final time GPS at video in milliseconds format or date format.
 *
 *
 * @param {object} videoSelected - The video object.
 * @param {string} format - Format to retur the time.
 * @returns {any} GPS time string, milliseconds or date format.
 * @throws Error if gps time is empty.
 */
export const getFinalGPSTimeVideo = (videoSelected, format) => {
  if (isNil(format) || isNil(videoSelected)) {
    throw new Error('format or videoSelected not defined');
  }
  let valueToReturn = '';
  const dateGPSRecordingVideo = new Date(videoOps.getTimeGPS(videoSelected));
  const durationVideo = videoOps.getDefaultDurationVideo(videoSelected);
  const finalTimeGPSRecording = dateGPSRecordingVideo.getTime() + durationVideo / 1000;
  const finalTimeRecordingDate = new Date(finalTimeGPSRecording);
  if (format === 'format-date') {
    valueToReturn = finalTimeRecordingDate.toUTCString();
  }
  if (format === 'format-milliseconds') {
    valueToReturn = finalTimeRecordingDate.getTime();
  }
  return valueToReturn;
};

/**
 * Calculationthe GPS time for one annotation.
 *
 * @param {object} videoSelected - The video object.
 * @param {object} annotation - The annotation object.
 * @returns {number} The video time gps.
 * @throws Error if something is wrong.
 */
export const calculateTimeGPSAnnotation = (videoSelected, annotation) => {
  if (isNil(videoSelected) || isNil(annotation)) {
    throw new Error('videoSelected or annotation not defined');
  }
  const dateGPSReference = new Date('1980-01-06T00:00:00Z');
  const dateGPSRecordingVideo = new Date(videoOps.getTimeGPS(videoSelected));
  const startTimeGPSRecording = dateGPSRecordingVideo.getTime() - dateGPSReference.getTime();
  const timeAnnotation = annotationOps.getVideoTime(annotation);
  const newTimeGPS = timeAnnotation / 1000 + startTimeGPSRecording;
  return newTimeGPS;
};

/**
 * Calculation the time by gps time for one annotation.
 *
 * @param {object} videoSelected - The video object.
 * @param {object} annotation - The annotation object.
 * @param {Array} annotations - The annotations.
 * @returns {number} The video time gps.
 * @throws Error if something is wrong.
 */
export const calculateTimeByGPSAnnotation = (videoSelected, annotation, annotations) => {
  if (isNil(videoSelected) || isNil(annotation) || isNil(annotations)) {
    throw new Error('videoSelected or annotation not defined');
  }
  let newTime = 0;
  const dateGPSReference = new Date('1980-01-06T00:00:00Z');
  const dateGPSRecordingVideo = new Date(videoOps.getTimeGPS(videoSelected));
  const startTimeGPSRecording = dateGPSRecordingVideo.getTime() - dateGPSReference.getTime();
  const videoDuration = videoOps.getDefaultDurationVideo(videoSelected);
  const { timeGPS } = annotation;
  if (isNil(timeGPS)) {
    throw new Error(
      'The annotations do not have the timeGPS property, please export the annotations with GPS time.'
    );
  }

  if (typeof timeGPS === 'number') {
    newTime = timeGPS - startTimeGPSRecording;
  }
  if (typeof timeGPS === 'string') {
    const timeGPSDate = new Date(timeGPS);
    const newTimeGPS = timeGPSDate.getTime() - dateGPSReference.getTime();

    newTime = newTimeGPS - startTimeGPSRecording;
  }
  const checkTime = newTime > videoDuration / 1000 ? -1 : checkTimeAnnotation(newTime, annotations);
  return checkTime;
};

/**
 * Calculation the distance for one annotation in featuresArray for synchronization at click in map.
 *
 * @param {Array} featureArray - The array with points to sunchronization track.
 * @param {number} currentFeatureIndex - The index of point in featureArray that no concordate with annotation.
 * @param {object} annotation - The annotation object.
 * @returns {number} The distance for the annotation in the track.
 * @throws Error if something is wrong.
 */
export const calculatePointDistanceInFeatures = (featureArray, currentFeatureIndex, annotation) => {
  if (isNil(featureArray) || isNil(annotation) || isNil(currentFeatureIndex)) {
    throw new Error('featureArray, annotation or currentFeatureIndex not defined');
  }
  const distanceFeature = featureArray[currentFeatureIndex].properties.distanceTravelled;
  const timeFeature = featureArray[currentFeatureIndex].properties.timeUS;
  const timeAnnotation = annotationOps.getVideoTime(annotation);
  const newDistance = (timeAnnotation * distanceFeature) / timeFeature;
  return newDistance;
};

/**
 * Transform seconds to string format.
 *
 * @param {number} seconds - Number of seconds to convert.
 * @returns {string} The time in string format.
 * @throws Error if seconds is empty.
 */
export const secondsToString = seconds => {
  if (isNil(seconds)) {
    throw new Error('seconds not defined');
  }
  let hour = Math.floor(seconds / 3600);
  hour = hour < 10 ? `0${hour}` : hour;
  let minute = Math.floor((seconds / 60) % 60);
  minute = minute < 10 ? `0${minute}` : minute;
  let second = seconds % 60;
  second = second < 10 ? `0${second}` : second;
  return `${hour}:${minute}:${second}`;
};
